import {
  Download,
  FileDown,
  FileText,
  Folder,
  Search,
  Send,
  Settings,
  Signature,
  Trash2,
  Upload,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loader } from "@/components/common/loader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { openDocument } from "@/features/documents/api";
import { deleteLetter, resolveMergeFields } from "@/features/letters/api";
import { LetterWizardDialog } from "@/features/letters/components/letter-wizard-dialog";
import { useClientLetters } from "@/features/letters/hooks/use-client-letters";
import type { Letter, LetterStatus, LetterTemplate } from "@/features/letters/types";
import { useActiveClient } from "@/layouts/client-layout";
import { cn } from "@/lib/utils";

const RECIPIENT_LABEL: Record<string, string> = {
  IRS: "IRS Correspondence",
  CLIENT: "Client Letters",
  OTHER: "Other",
};
const RECIPIENT_ORDER = ["IRS", "CLIENT", "OTHER"];

const statusBadgeClassName: Record<LetterStatus, string> = {
  FINAL: "border-success/20 bg-success/10 text-success",
  DRAFT: "border-border bg-muted text-muted-foreground",
};

/** Ordered, unique `{token}` names in a template body/subject. */
function tokenNames(...texts: string[]): string[] {
  const seen = new Set<string>();
  for (const text of texts) {
    for (const m of text.matchAll(/\{([a-z0-9_]+)(?::[A-Za-z]!?)?\}/gi)) {
      if (m[1]) seen.add(m[1]);
    }
  }
  return [...seen];
}

/** Render a template body as a plain-text preview with {token}s highlighted.
 * Tags are stripped (never dangerouslySetInnerHTML). */
function renderTemplatePreview(body: string) {
  const text = body.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const parts = text.split(/(\{[a-z0-9_]+(?::[A-Za-z]!?)?\})/gi);
  return parts.map((part, i) =>
    /^\{[a-z0-9_]+(?::[A-Za-z]!?)?\}$/i.test(part) ? (
      <span
        key={i}
        className="border-primary/20 bg-primary/5 text-primary rounded border px-1"
      >
        {part}
      </span>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}

export default function ClientLettersPage() {
  const { client } = useActiveClient();
  const navigate = useNavigate();
  const { templates, letters, loading, refetch } = useClientLetters(client.id);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Letter | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Fill-in field names for the selected template, so Available Variables can
  // split "you fill these in" from "filled automatically".
  const [fieldNames, setFieldNames] = useState<Set<string>>(new Set());

  const clientMarried = client.maritalStatus === "MARRIED";

  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const visible = templates.filter(
      (t) => t.isActive !== false && (!term || t.name.toLowerCase().includes(term)),
    );
    return RECIPIENT_ORDER.map((recipient) => ({
      recipient,
      label: RECIPIENT_LABEL[recipient],
      templates: visible.filter((t) => t.recipient === recipient),
    })).filter((g) => g.templates.length > 0);
  }, [templates, search]);

  const selected: LetterTemplate | null =
    templates.find((t) => t.id === selectedId) ?? templates[0] ?? null;

  // Resolve the selected template's fill-in fields (for the Variables pane).
  useEffect(() => {
    if (!selected) {
      setFieldNames(new Set());
      return;
    }
    let active = true;
    resolveMergeFields(selected.id, client.id)
      .then((res) => active && setFieldNames(new Set(res.fields.map((f) => f.name))))
      .catch(() => active && setFieldNames(new Set()));
    return () => {
      active = false;
    };
  }, [selected?.id, client.id]);

  const { fillTokens, autoTokens } = useMemo(() => {
    if (!selected) return { fillTokens: [], autoTokens: [] as string[] };
    const names = tokenNames(selected.body, selected.subject);
    return {
      fillTokens: names.filter((n) => fieldNames.has(n)),
      autoTokens: names.filter((n) => !fieldNames.has(n)),
    };
  }, [selected?.id, selected?.body, selected?.subject, fieldNames]);

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteLetter(deleteTarget.id);
      toast.success("Letter deleted");
      setDeleteTarget(null);
      refetch();
    } catch {
      toast.error("Couldn't delete the letter.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-foreground mb-2 text-3xl font-bold">Letters</h1>
          <p className="text-muted-foreground max-w-2xl">
            Generate professional client correspondence using reusable templates.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/settings/letters")}>
            <Settings /> Manage Templates
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled
            title="Template import is coming in a later release."
          >
            <Upload /> Upload Template
          </Button>
          <Button size="sm" disabled={!selected} onClick={() => setWizardOpen(true)}>
            <FileDown /> Fill &amp; Generate
          </Button>
        </div>
      </div>

      {/* Workspace: template library / preview / variables & options */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        {/* Template library */}
        <div className="lg:col-span-3">
          <Card className="h-full gap-3 p-4">
            <h3 className="text-foreground text-lg font-semibold">Template Library</h3>
            <div className="relative">
              <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                type="text"
                placeholder="Search templates..."
                className="h-9 pl-9 text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {loading ? (
              <div className="flex justify-center py-6">
                <Loader />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {grouped.map((folder) => (
                  <div key={folder.recipient}>
                    <div className="text-muted-foreground mb-2 flex items-center gap-2 px-2 py-1 text-xs font-semibold tracking-wider uppercase">
                      <Folder className="size-4" />
                      {folder.label}
                    </div>
                    <div className="flex flex-col gap-2">
                      {folder.templates.map((template) => {
                        const active = selected?.id === template.id;
                        return (
                          <button
                            key={template.id}
                            type="button"
                            onClick={() => setSelectedId(template.id)}
                            className={cn(
                              "flex items-center gap-2 rounded-lg border px-3 py-3 text-left transition-colors",
                              active
                                ? "border-primary/30 bg-primary/5"
                                : "border-border bg-card hover:border-primary/30 hover:bg-muted",
                            )}
                          >
                            <FileText
                              className={cn(
                                "size-5 shrink-0",
                                active ? "text-primary" : "text-muted-foreground",
                              )}
                            />
                            <div className="flex flex-col">
                              <span
                                className={cn(
                                  "text-sm font-medium",
                                  active ? "text-primary font-bold" : "text-foreground",
                                )}
                              >
                                {template.name}
                              </span>
                              {template.isSystem && (
                                <span className="text-primary/70 text-xs">System template</span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {grouped.length === 0 && (
                  <p className="text-muted-foreground px-2 text-sm">No templates found.</p>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* Template preview */}
        <div className="lg:col-span-6">
          <Card className="h-full gap-0 overflow-hidden p-0">
            <div className="flex h-12 shrink-0 items-center justify-between border-b px-4">
              <span className="text-muted-foreground text-sm font-medium">
                {selected ? selected.name : "Template preview"}
              </span>
              <Button
                size="sm"
                disabled={!selected}
                onClick={() => setWizardOpen(true)}
              >
                <FileDown /> Fill &amp; Generate
              </Button>
            </div>

            <div className="bg-muted/40 flex flex-1 justify-center overflow-y-auto p-6">
              <div className="bg-card text-foreground min-h-[600px] w-full max-w-[600px] border p-10 text-[13px] leading-relaxed">
                {selected ? (
                  <div className="space-y-4">
                    <div className="text-muted-foreground text-xs">
                      Preview of the template body. Merge tokens are filled from the
                      client's data and your inputs when you generate.
                    </div>
                    <div className="pt-2 font-bold">RE: {selected.subject}</div>
                    <p className="whitespace-pre-wrap">{renderTemplatePreview(selected.body)}</p>
                  </div>
                ) : (
                  <p className="text-muted-foreground">
                    Select a template from the library to preview it here.
                  </p>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Variables & generation options */}
        <div className="flex flex-col gap-6 lg:col-span-3">
          <Card className="h-full gap-0 overflow-hidden p-0">
            <div className="border-b p-4">
              <h3 className="text-foreground text-lg font-semibold">Available Variables</h3>
              <p className="text-muted-foreground mt-1 text-xs">
                {selected
                  ? "Tokens used by this template."
                  : "Select a template to see its tokens."}
              </p>
            </div>
            <div className="flex flex-col gap-4 p-4">
              <div>
                <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wider uppercase">
                  You fill in
                </h4>
                {fillTokens.length ? (
                  <div className="flex flex-wrap gap-2">
                    {fillTokens.map((v) => (
                      <span
                        key={v}
                        className="border-primary/30 bg-primary/5 text-primary rounded border px-2 py-1 text-xs"
                      >
                        {`{${v}}`}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-xs">No fill-in fields.</p>
                )}
              </div>
              <div>
                <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wider uppercase">
                  Filled automatically
                </h4>
                {autoTokens.length ? (
                  <div className="flex flex-wrap gap-2">
                    {autoTokens.map((v) => (
                      <span
                        key={v}
                        className="border-border text-muted-foreground rounded border px-2 py-1 text-xs"
                      >
                        {`{${v}}`}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-xs">None.</p>
                )}
              </div>
            </div>
          </Card>

          <Card className="gap-4 p-4">
            <h3 className="text-foreground text-lg font-semibold">Generation Options</h3>
            <div className="flex flex-col gap-3">
              <Button
                size="sm"
                className="h-11 w-full"
                disabled={!selected}
                onClick={() => setWizardOpen(true)}
              >
                <FileDown /> Fill &amp; Generate PDF
              </Button>
              <Button variant="outline" size="sm" className="h-11 w-full" disabled>
                <FileText /> Download DOCX
              </Button>
              <div className="flex gap-2 border-t pt-3">
                <Button variant="outline" size="sm" className="h-10 flex-1 text-xs" disabled>
                  <Send /> Send to Portal
                </Button>
                <Button variant="outline" size="sm" className="h-10 flex-1 text-xs" disabled>
                  <Signature /> Req. Signature
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Recent letters */}
      <Card className="gap-0 overflow-hidden p-0">
        <div className="flex items-center justify-between border-b p-5">
          <h3 className="text-foreground text-lg font-semibold">Recent Letters Generated</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="text-muted-foreground p-4 text-xs font-medium tracking-wider uppercase">
                Letter Name
              </TableHead>
              <TableHead className="text-muted-foreground p-4 text-xs font-medium tracking-wider uppercase">
                Generated Date
              </TableHead>
              <TableHead className="text-muted-foreground p-4 text-xs font-medium tracking-wider uppercase">
                Template
              </TableHead>
              <TableHead className="text-muted-foreground p-4 text-right text-xs font-medium tracking-wider uppercase">
                Status
              </TableHead>
              <TableHead className="text-muted-foreground w-28 p-4 text-center text-xs font-medium tracking-wider uppercase">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {letters.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground p-6 text-center">
                  No letters generated yet. Pick a template and click Fill &amp; Generate.
                </TableCell>
              </TableRow>
            ) : (
              letters.map((letter) => (
                <TableRow key={letter.id} className="group h-14">
                  <TableCell className="text-foreground flex items-center gap-2 p-4 font-medium">
                    <FileText className="text-destructive size-5" />
                    {letter.subject}
                  </TableCell>
                  <TableCell className="text-muted-foreground p-4">
                    {new Date(letter.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="p-4">{letter.templateName || "—"}</TableCell>
                  <TableCell className="p-4 text-right">
                    <Badge variant="outline" className={statusBadgeClassName[letter.status]}>
                      {letter.statusDisplay}
                    </Badge>
                  </TableCell>
                  <TableCell className="p-4 text-center">
                    <div className="flex items-center justify-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Open ${letter.subject}`}
                        disabled={!letter.document}
                        onClick={() =>
                          letter.document &&
                          openDocument(letter.document.id).catch(() =>
                            toast.error("Couldn't open the PDF."),
                          )
                        }
                      >
                        <Download />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Delete ${letter.subject}`}
                        onClick={() => setDeleteTarget(letter)}
                      >
                        <Trash2 className="text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {selected && (
        <LetterWizardDialog
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          template={selected}
          clientId={client.id}
          clientMarried={clientMarried}
          onGenerated={refetch}
          onOpenDocument={(id) =>
            openDocument(id).catch(() =>
              toast.error("Generated, but couldn't open the PDF. Find it under Documents."),
            )
          }
        />
      )}

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this letter?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleteTarget?.subject}” will be removed from this list. The generated
              PDF stays in the client's Documents archive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleting}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
